(function () {
  class MelalightRitualCarousel {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-ugc-track]');
      this.slides = Array.from(section.querySelectorAll('[data-ugc-slide]'));
      this.autoplay = section.dataset.autoplay !== 'false';
      this.activeSlide = null;
      this.scrollRaf = null;
      this.advanceTimeout = null;
      this.snapRestoreTimeout = null;
      this.isAdvancing = false;
      this.totalSlides = this.slides.length;

      if (!this.track || this.slides.length === 0) return;

      this.bindEvents();
      this.centerInitialSlide();
      this.updateActiveSlide();
    }

    bindEvents() {
      this.track.addEventListener('scroll', () => {
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => this.updateActiveSlide());
      });

      this.section.addEventListener('click', (event) => {
        const muteButton = event.target.closest('[data-ugc-mute]');
        const playButton = event.target.closest('[data-ugc-play]');
        const productLink = event.target.closest('a');

        if (muteButton) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleMute(muteButton);
          return;
        }

        if (playButton) {
          event.preventDefault();
          event.stopPropagation();
          this.togglePlay(playButton);
          return;
        }

        if (productLink) return;

        const slide = event.target.closest('[data-ugc-slide]');
        if (slide && slide !== this.activeSlide) {
          this.scrollToSlide(slide);
        }
      });

      window.addEventListener(
        'resize',
        this.debounce(() => {
          this.updateActiveSlide();
        }, 150)
      );

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;

        video.addEventListener('ended', () => {
          if (slide === this.activeSlide) {
            this.advanceToNextSlide();
          }
        });
      });
    }

    advanceToNextSlide() {
      if (!this.autoplay || !this.activeSlide || this.slides.length === 0) return;

      if (this.slides.length === 1) {
        const video = this.activeSlide.querySelector('video');
        if (!video) return;
        video.currentTime = 0;
        video.play().catch(() => {});
        return;
      }

      const currentIndex = this.slides.indexOf(this.activeSlide);
      const nextSlide = this.slides[(currentIndex + 1) % this.slides.length];

      this.isAdvancing = true;

      this.scrollToSlide(nextSlide, true, () => {
        this.scrollToSlide(nextSlide, false);
        this.isAdvancing = false;
        this.setActiveSlide(nextSlide);
      });
    }

    getSlideScrollLeft(slide) {
      const track = this.track;
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const targetScrollLeft =
        track.scrollLeft + (slideRect.left - trackRect.left) - (trackRect.width - slideRect.width) / 2;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

      return Math.max(0, Math.min(targetScrollLeft, maxScroll));
    }

    setScrollSnapEnabled(enabled) {
      this.track.style.scrollSnapType = enabled ? '' : 'none';
    }

    restoreScrollSnap() {
      clearTimeout(this.snapRestoreTimeout);
      this.setScrollSnapEnabled(true);
    }

    afterScrollComplete(callback, smooth) {
      clearTimeout(this.snapRestoreTimeout);

      if (smooth && 'onscrollend' in this.track) {
        this.track.addEventListener(
          'scrollend',
          () => {
            this.restoreScrollSnap();
            callback();
          },
          { once: true }
        );
        return;
      }

      this.snapRestoreTimeout = setTimeout(() => {
        this.restoreScrollSnap();
        callback();
      }, smooth ? 600 : 0);
    }

    centerInitialSlide() {
      const initialIndex = Math.floor(this.slides.length / 2);
      const targetSlide = this.slides[initialIndex];

      if (!targetSlide) return;

      requestAnimationFrame(() => {
        this.scrollToSlide(targetSlide, false);
      });
    }

    scrollToSlide(slide, smooth = true, onComplete) {
      const track = this.track;
      const targetScrollLeft = this.getSlideScrollLeft(slide);

      this.setScrollSnapEnabled(false);

      track.scrollTo({
        left: targetScrollLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });

      if (!smooth) {
        this.restoreScrollSnap();
        onComplete?.();
        return;
      }

      this.afterScrollComplete(() => {
        onComplete?.();
      }, true);
    }

    updateActiveSlide() {
      if (this.isAdvancing) return;

      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;

      let closestSlide = this.slides[0];
      let closestDistance = Infinity;

      this.slides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(center - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      if (closestSlide === this.activeSlide) return;

      this.setActiveSlide(closestSlide);
    }

    setActiveSlide(slide) {
      this.slides.forEach((item, index) => {
        const isActive = item === slide;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }

        item.setAttribute(
          'aria-label',
          `Video ${index + 1} of ${this.totalSlides}`
        );
      });

      this.activeSlide = slide;
      this.syncVideos();
    }

    syncVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        const playButton = slide.querySelector('[data-ugc-play]');

        if (!video) return;

        if (slide === this.activeSlide && this.autoplay) {
          video.muted = slide.dataset.muted !== 'false';
          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise.catch(() => {
              video.pause();
              this.setPlayState(playButton, false);
            });
          }

          this.setPlayState(playButton, !video.paused);
        } else {
          video.pause();
          this.setPlayState(playButton, false);
        }
      });
    }

    toggleMute(button) {
      const slide = button.closest('[data-ugc-slide]');
      const video = slide?.querySelector('video');

      if (!slide || !video) return;

      video.muted = !video.muted;
      slide.dataset.muted = video.muted ? 'true' : 'false';
      button.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
      button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      button.classList.toggle('is-muted', video.muted);
    }

    togglePlay(button) {
      const slide = button.closest('[data-ugc-slide]');
      const video = slide?.querySelector('video');

      if (!slide || !video || slide !== this.activeSlide) return;

      if (video.paused) {
        video.play().then(() => this.setPlayState(button, true));
      } else {
        video.pause();
        this.setPlayState(button, false);
      }
    }

    setPlayState(button, isPlaying) {
      if (!button) return;
      button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      button.classList.toggle('is-playing', isPlaying);
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  function initRitualCarousels() {
    document.querySelectorAll('[data-melalight-ritual]').forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';
      new MelalightRitualCarousel(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRitualCarousels);
  } else {
    initRitualCarousels();
  }

  document.addEventListener('shopify:section:load', initRitualCarousels);
})();
